import React, { useState, useEffect } from 'react';
import { Typography, Container, Box, Grid, Card, CardContent, CircularProgress, Alert, Button, useMediaQuery, useTheme } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { getAnalyticsOverview, getTaxProgress } from '../services/api';
import { Link } from 'react-router-dom';
import { FormControlLabel, Checkbox } from '@mui/material';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

function AnalyticsOverviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartView, setChartView] = useState('all');
  const [excludeTaxSetAside, setExcludeTaxSetAside] = useState(true);
  const [taxSetAsideAmount, setTaxSetAsideAmount] = useState(0);

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, taxRes] = await Promise.all([
        getAnalyticsOverview(),
        getTaxProgress().catch(() => null),
      ]);
      setData(analyticsRes.data);
      if (taxRes?.data?.projected_setaside) {
        setTaxSetAsideAmount(taxRes.data.projected_setaside);
      }
    } catch (err) {
      console.error('Failed to fetch analytics overview', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value) => {
    const isPositive = value >= 0;
    const Icon = isPositive ? TrendingUpIcon : TrendingDownIcon;
    const color = isPositive ? 'success.main' : 'error.main';
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', color }}>
        <Icon sx={{ mr: 0.5, fontSize: 16 }} />
        {Math.abs(value).toFixed(1)}%
      </Box>
    );
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Financial Analytics Overview
        </Typography>

        <Box sx={{ mb: 3 }}>
  <Button 
    variant="contained"
    sx={{ mr: 1 }}
  >
    Overview
  </Button>
  <Button 
    variant="outlined"
    component={Link} 
    to="/analytics/categories"
    sx={{ mr: 1 }}
  >
    Category Insights
  </Button>
  <Button 
    variant="outlined"
    component={Link} 
    to="/analytics/accounts"
  >
    Account Performance
  </Button>
</Box>

{/* NEW: Chart View Toggle */}
<Box sx={{ mb: { xs: 2, md: 4 } }}>
  <Button 
    variant={chartView === 'all' ? 'contained' : 'outlined'}
    onClick={() => setChartView('all')}
    sx={{ mr: 1 }}
  >
    All Charts
  </Button>
  <Button 
    variant={chartView === 'balance' ? 'contained' : 'outlined'}
    onClick={() => setChartView('balance')}
    sx={{ mr: 1 }}
  >
    Balance Trend
  </Button>
  <Button 
    variant={chartView === 'categories' ? 'contained' : 'outlined'}
    onClick={() => setChartView('categories')}
    sx={{ mr: 1 }}
  >
    Spending Categories
  </Button>
  <Button 
    variant={chartView === 'transactions' ? 'contained' : 'outlined'}
    onClick={() => setChartView('transactions')}
  >
    Recent Transactions
  </Button>
</Box>

        {/* Key Metrics Cards */}
        <Grid container spacing={{ xs: 4, md: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', p: { xs: 1, md: 2 } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccountBalanceIcon color="primary" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="h6">
                    Net Worth
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {formatCurrency(
                    excludeTaxSetAside
                      ? data.key_metrics.net_worth - taxSetAsideAmount
                      : data.key_metrics.net_worth
                  )}
                </Typography>
                {taxSetAsideAmount > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={excludeTaxSetAside}
                          onChange={(e) => setExcludeTaxSetAside(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption" color="text.secondary">
                          Excl. tax set-aside ({formatCurrency(taxSetAsideAmount)})
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', p: { xs: 1, md: 2 } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoneyIcon color="success" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="h6">
                    Monthly Income
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {formatCurrency(data.key_metrics.monthly_income)}
                </Typography>
                {formatPercentage(data.key_metrics.income_growth)}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', p: { xs: 1, md: 2 } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoneyIcon color="error" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="h6">
                    Monthly Expenses
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {formatCurrency(data.key_metrics.monthly_expenses)}
                </Typography>
                {formatPercentage(data.key_metrics.expense_growth)}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', p: { xs: 1, md: 2 } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="h6">
                    Net Cash Flow
                  </Typography>
                </Box>
                <Typography variant="h4" color={data.key_metrics.net_cash_flow >= 0 ? 'success.main' : 'error.main'}>
                  {formatCurrency(data.key_metrics.net_cash_flow)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Conditional Layout Based on Chart View */}
{chartView === 'all' ? (
  <Grid container spacing={3} sx={{ mt: { xs: 2, md: 55 / 8 } }}>
    {/* Balance Trend Chart */}
    <Grid item xs={12} md={8}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            30-Day Balance Trend
          </Typography>
          <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
            <LineChart data={data.balance_trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <YAxis tickFormatter={(value) => formatCurrency(value)} />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                formatter={(value) => [formatCurrency(value), 'Balance']}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="balance" 
                stroke="#8884d8" 
                strokeWidth={2}
                name="Account Balance"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Grid>

    {/* Top Spending Categories */}
    <Grid item xs={12} md={4}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Spending Categories
          </Typography>
          <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
            <PieChart>
              <Pie
                data={data.top_spending_categories}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="amount"
              >
                {data.top_spending_categories.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Grid>

    {/* Recent Transactions */}
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Transactions
          </Typography>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {data.recent_transactions.map((transaction) => (
              <Box
                key={transaction.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box>
                  <Typography variant="body1">
                    {transaction.description}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {transaction.category_name} • {new Date(transaction.transaction_date).toLocaleDateString()}
                  </Typography>
                </Box>
                <Typography
                  variant="h6"
                  color={transaction.amount >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(transaction.amount)}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
) : (
  /* Single Chart View - Full Width */
  <Box sx={{ width: '100%' }}>
    {chartView === 'balance' && (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            30-Day Balance Trend
          </Typography>
          <ResponsiveContainer width="100%" height={600}>
            <LineChart data={data.balance_trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <YAxis tickFormatter={(value) => formatCurrency(value)} />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                formatter={(value) => [formatCurrency(value), 'Balance']}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="balance" 
                stroke="#8884d8" 
                strokeWidth={3}
                name="Account Balance"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    )}

    {chartView === 'categories' && (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Spending Categories
          </Typography>
          <ResponsiveContainer width="100%" height={600}>
            <PieChart>
              <Pie
                data={data.top_spending_categories}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                outerRadius={150}
                fill="#8884d8"
                dataKey="amount"
              >
                {data.top_spending_categories.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    )}

    {chartView === 'transactions' && (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Transactions
          </Typography>
          <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
            {data.recent_transactions.map((transaction) => (
              <Box
                key={transaction.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                    {transaction.description}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {transaction.category_name} • {new Date(transaction.transaction_date).toLocaleDateString()}
                  </Typography>
                </Box>
                <Typography
                  variant="h6"
                  color={transaction.amount >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(transaction.amount)}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    )}
  </Box>
)}
      </Box>
    </Container>
  );
}

export default AnalyticsOverviewPage;